import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export default function PartnerInviteAccept() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<"idle" | "working" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const next = encodeURIComponent(`/partner/invite/${token}`);
      navigate(`/auth?redirect=${next}`, { replace: true });
    }
  }, [loading, user, token, navigate]);

  const accept = async () => {
    if (!token) return;
    setState("working");
    const { error } = await supabase.rpc("accept_partner_invite" as any, { _token: token });
    if (error) {
      setState("error");
      setMessage(error.message);
      toast.error(error.message);
      return;
    }
    setState("done");
    toast.success("Welcome to the team!");
    setTimeout(() => navigate("/partner"), 800);
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="pt-36 pb-20 container mx-auto px-6 max-w-lg">
        <Card className="p-8 space-y-4 text-center">
          <h1 className="font-display text-2xl">Join a partner organization</h1>
          <p className="text-sm text-muted-foreground">
            Accepting this invite gives you partner admin access — you'll be able to manage the org profile, leads, resources, and team.
          </p>

          {state === "error" && (
            <p className="text-sm text-destructive">{message}</p>
          )}
          {state === "done" ? (
            <p className="text-sm text-primary">You're in. Redirecting…</p>
          ) : (
            <Button onClick={accept} disabled={state === "working" || !user}>
              {state === "working" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Accept invite
            </Button>
          )}
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}